/**
 * Provides a context for managing state and selectors related to VMS Control components
 * @module VMSControlContext
 */
import React, { createContext, useContext, useRef, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FORM_MODE, REST_API, STATE_STATUS } from '@awp/constants';
import { BOOTSTRAP_BREAKPOINTS, checkBreakpoint, getBreakpoints } from '@awp/helpers/UIHelper';
import { debounce } from '@awp/helpers/Utils';
import { GetJson } from '@awp/helpers/fetchReact';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import {
  fetchDetailedVMSInfo,
  selectStatus,
  selectStatusError,
  selectVMSDetails,
} from '@awp/stores/variableMessageSignSlice';

/**
 * Context for managing state and selectors related to VMS control components
 * @type {React.Context}
 */
const VMSControlContext = createContext();

/**
 * Provider component for MultiStationContext.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Child components wrapped by the provider.
 * @returns {React.ReactNode} Provider component.
 */
export const VMSControlContextProvider = ({ children }) => {
  const dispatch = useDispatch();

  // Refresh timer interval (adjustable)
  const refreshInterval = 60000; // 1 minute

  const [currentBreakpoint, setCurrentBreakpoint] = useState(BOOTSTRAP_BREAKPOINTS.LARGE);
  const [formMode, setFormMode] = useState(FORM_MODE.LIST);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [countdown, setCountdown] = useState();
  const [pageIndex, setPageIndex] = useState(0);
  const [showSetDateTimeModal, setShowSetDateTimeModal] = useState(false);
  const [variableMessageSign, setVariableMessageSign] = useState({ weatherStationId: null, vmsSignId: null });

  const intervalRef = useRef(null);

  const status = useSelector(selectStatus);
  const VMSData = useSelector(selectVMSDetails);
  const statusError = useSelector(selectStatusError);

  useEffect(() => {
    const handleResize = debounce(() => {
      setCurrentBreakpoint(getBreakpoints());
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (status.stateSet === STATE_STATUS.LOADING || status.stateTimer === STATE_STATUS.LOADING) {
      removeInterval();
    }
    if (status.stateSet === STATE_STATUS.FAILED || status.stateTimer === STATE_STATUS.FAILED) {
      // Set a new interval and store its ID
      intervalRef.current = setInterval(fetchData, refreshInterval);
    }
  }, [status]);

  /**
   * Fetch data from the server and update the state.
   * Handles loading state and errors.
   */
  const fetchData = async () => {
    setLoading(true); // Set loading state to true when data fetching starts
    console.info('refreshing VMS');
    try {
      await dispatch(fetchDetailedVMSInfo());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false); // Set loading state to false when data fetching completes
    }
  };

  const removeInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  useEffect(() => {
    if (loading) return;

    if (status.detailedInfo === STATE_STATUS.LOADING) return;

    fetchData(); //fire the first time

    // Clear the previous interval if it exists
    removeInterval();
    // Set a new interval and store its ID
    intervalRef.current = setInterval(fetchData, refreshInterval);

    // return () => {
    //   removeInterval();
    //   setLoading(false);
    // };
  }, [VMSData]);

  /**
   * Checks if the search term is present in the provided source string.
   * @param {string} src - The source string to search within.
   * @returns {boolean} True if the search term is found, false otherwise.
   */
  const hasSearch = (src) => {
    return src ? src.toLowerCase().includes(search.toLowerCase()) : false;
  };

  const filteredData = useMemo(() => {
    let filtered = [...VMSData];
    if (!isValueEmpty(search)) {
      filtered = filtered.filter((man) => hasSearch(man.Name) || hasSearch(man.Note));
    }

    return filtered;
  }, [search, VMSData]);

  // Structure the context values for the provider
  const stateValues = {
    formMode,
    loading,
    variableMessageSign,
    search,
    currentBreakpoint,
    pageIndex,
    showSetDateTimeModal,
  };

  const stateSetters = {
    setFormMode,
    setLoading,
    setVariableMessageSign,
    setSearch,
    setShowSetDateTimeModal,
  };

  const selectorValues = {
    VMSData,
    statusError,
    status,
  };

  const memoizedValues = { filteredData };

  // Combine values into a single context value
  const contextValue = {
    ...stateValues,
    ...stateSetters,
    ...selectorValues,
    ...memoizedValues,
    removeInterval,
  };

  return <VMSControlContext.Provider value={contextValue}>{children}</VMSControlContext.Provider>;
};

/**
 * Hook for consuming MultiStationContext.
 * @returns {object} Context value.
 */
export const useVMSControlContext = () => useContext(VMSControlContext);
